/**
 * Comprehensive list of resource types from ResourceTypeRegistry.cs
 * Shared between MigrationSetup and SnapshotSetup pages
 */

export interface ResourceTypeCategory {
  name: string;
  types: string[];
  labels?: Record<string, string>;
}

export const RESOURCE_TYPE_CATEGORIES: ResourceTypeCategory[] = [
  {
    name: 'Admin',
    types: [
      'User',
      'Group',
      'Location',
      'Station',
      'OAuth Client',
      'Group Roles',
      'Authorization Division',
      'Authorization Role',
      //'Authorization Product' Not supported as a resources in terraform
    ],
    labels: {
      'User': 'People',
      'Group': 'Groups',
      'Station': 'Phones',
      'OAuth Client': 'OAuth',
      'Authorization Division': 'Divisions',
      'Auth Division': 'Divisions',
      'Authorization Role': 'Roles',
      'Auth Role': 'Roles',
      //'Authorization Product': 'Subscriptions' Not supported as a resources in terraform
    }
  },
  {
    name: 'Architect',
    types: [
      'Architect Flow',
      'Architect Flow Milestone',
      'Architect Flow Outcome',
      'Architect Schedules',
      'Architect Schedule Groups',
      'Architect User Prompt',
      'Architect Datatable',
      'Architect Datatable (with Data)',
      'Architect Emergency Group',
      'Architect IVR',
      'Architect Grammar',
      'Architect Grammar Language'
    ],
    labels: {
      'Architect Flow': 'Flows',
      'Architect Flow Milestone': 'Flow Milestone',
      'Architect Flow Outcome': 'Flow Outcome',
      'Architect Schedules': 'Operating Schedule',
      'Architect Schedule Groups': 'Operating Schedule Group',
      'Architect User Prompt': 'User Prompts',
      'Architect Datatable': 'Data Table Schema',
      'Architect Datatable (with Data)': 'Data Table (with Data)',
      'Architect Emergency Group': 'Emergency Group',
      'Architect IVR': 'Call Route',
      'Architect Grammar': 'Grammar',
      'Architect Grammar Language': 'Grammar Language'
    }
  },
  {
    name: 'Routing',
    types: [
      'Routing Queue',
      'Routing Wrapup Code',
      'Routing Skill',
      'Routing Language',
      'Routing Language Skill',
      'Routing Email Domain',
      'Routing Email Route',
      'Routing SMS Address',
      'Routing Utilization',
      'Routing Utilization Label',
    ],
    labels: {
      'Routing Queue': 'Queues',
      'Routing Wrapup Code': 'Wrap-up Codes',
      'Routing Skill': 'ACD Skills',
      'Routing Language': 'Supported Languages',
      'Routing Language Skill': 'Language Skill',
      'Routing Email Domain': 'Email Domain',
      'Routing Email Route': 'Email Routes',
      'Routing SMS Address': 'Message Routing',
      'Routing Utilization': 'Utilization',
      'Routing Utilization Label': 'Utilization Label'
    }
  },
  {
    name: 'Outbound',
    types: [
      'Outbound Campaign',
      'Outbound Call Analysis Response Set',
      'Outbound Contact List',
      'Outbound Contact List Filter',
      'Outbound Contact List Template',
      'Outbound DNC List',
      'Outbound Attempt Limit',
      'Outbound Callable Time Set',
      'Outbound Campaign Rule',
      'Outbound Digital Ruleset',
      'Outbound File Specification Template',
      'Outbound Import Template',
      'Outbound Messaging Campaign',
      'Outbound Ruleset',
      'Outbound Sequence',
      'Outbound Settings'
    ]
  },
  {
    name: 'Telephony',
    types: [
      'Telephony Phone',
      'Telephony Phone Base Settings',
      'Phone Base Settings',
      'Telephony Site',
      'Telephony Edge',
      'Telephony Edge Group',
      'Edge Group',
      'Telephony Trunk',
      'Telephony Trunk Base Settings',
      'Telephony Line Base Settings',
      'Telephony DID',
      'Telephony DID Pool',
      'Telephony Extension Pool',
      'Telephony Certification Authority',
      'Telephony Site Outbound Route',
    ],
    labels: {
      'Telephony Phone': 'Phone',
      'Telephony Phone Base Settings': 'Phone Base Settings',
      'Phone Base Settings': 'Phone Base Settings',
      'Telephony Edge': 'Premises Edges',
      'Telephony Edge Group': 'Edge Group',
      'Telephony Trunk': 'External Trunk or Phone Trunk',
      'Telephony Line Base Settings': 'Line Appearance',
      'Telephony DID': 'DID Number',
      'Telephony DID Pool': 'DID Range',
      'Telephony Extension Pool': 'Extension Pools',
      'Telephony Certification Authority': 'Certificate Authority',
      'Telephony Site Outbound Route': 'Outbound Route'
    }
  },
  {
    name: 'Interaction Assets',
    types: [
      'Script',
      'Widget Deployment',
      'Response Management Library',
      'Response Management Response',
      'Response Management Response Asset'
    ],
    labels: {
      'Widget Deployment': 'Widgets',
      'Response Management Library': 'Canned Response Libraries',
      'Response Management Response': 'Canned Responses',
      'Response Management Response Asset': 'Response Assets'
    }
  },
  {
    name: 'Integrations',
    types: [
      'Integration',
      'Integration Action',
      'Web Deployment',
      'Web Deployment Configuration'
    ],
    labels: {
      'Integration': 'Integrations',
      'Integration Action': 'Data Actions',
      'Web Deployment': 'Web Messenger Deployments',
      'Web Deployment Configuration': 'Web Messenger Configurations'
    }
  },
  {
    name: 'Quality Management',
    types: [
      'Quality Evaluation Form',
      'Quality Survey Form'
    ],
    labels: {
      'Quality Evaluation Form': 'Evaluation Forms',
      'Quality Survey Form': 'Survey Form'
    }
  },
  {
    name: 'Recording and Policies',
    types: [
      'Recording Media Retention Policy'
    ],
    labels: {
      'Recording Media Retention Policy': 'Reorder Policies'
    }
  },
  {
    name: 'Speech and Text Analytics',
    types: [
      'Analytics Reporting Settings'
    ],
    labels: {
      'Analytics Reporting Settings': 'Analytics Settings'
    }
  },
  {
    name: 'Knowledge',
    types: [
      'Knowledge Base',
      'Knowledge Document',
      'Knowledge Document Variation',
      'Knowledge Category',
      'Knowledge Label'
    ],
    labels: {
      'Knowledge Document': 'Knowledge Articles',
      'Knowledge Document Variation': 'Knowledge Variation',
      'Knowledge Label': 'Knowledge Labels'
    }
  },
  {
    name: 'Employee Engagement',
    types: [
      'Employee Performance External Metrics'
    ],
    labels: {
      'Employee Performance External Metrics': 'External Metric'
    }
  },
  {
    name: 'Predictive Engagement',
    types: [
      'Journey Action Map',
      'Journey Action Template',
      'Journey Outcome',
      'Journey Segment'
    ],
    labels: {
      'Journey Action Map': 'Journey Action Maps',
      'Journey Action Template': 'Journey Action Library',
      'Journey Outcome': 'Journey Outcomes',
      'Journey Segment': 'Segments'
    }
  },
  {
    name: 'Messaging',
    types: [
      'Messaging Integration Open',
      'Messaging Integration WhatsApp',
      'Messaging Settings Supported Content'
    ],
    labels: {
      'Messaging Integration Open': 'Open Messaging',
      'Messaging Integration WhatsApp': 'WhatsApp Messenger'
    }
  },
  {
    name: 'Contact Management',
    types: [
      'External Contact',
      'External Organization'
    ],
    labels: {
      'External Contact': 'Contacts',
      'External Organization': 'Organizations'
    }
  },
  {
    name: 'Organization',
    types: [
      'Organization Authentication Settings',
      'Organization Me',
      'Presence Definition',
      'Process Automation Trigger'
    ],
    labels: {
      'Organization Authentication Settings': 'Authentication',
      'Organization Me': 'Settings',
      'Process Automation Trigger': 'Trigger',
      'Presence Definition' : 'Status'
    }
  }
];

/**
 * Returns the display label for a resource type within a known category.
 * Falls back to the API name if no label is defined.
 */
export function getTypeLabel(category: ResourceTypeCategory, type: string): string {
  return category.labels?.[type] ?? type;
}

/**
 * Returns the display label for a resource type by API name alone.
 * Falls back to the API name if no label is defined.
 */
export function getTypeLabelByApiName(apiName: string): string {
  for (const category of RESOURCE_TYPE_CATEGORIES) {
    const label = category.labels?.[apiName];
    if (label !== undefined) return label;
  }
  return apiName;
}

// Flat list for backward compatibility
export const RESOURCE_TYPES = RESOURCE_TYPE_CATEGORIES.flatMap((category) => category.types);

export type ResourceType = (typeof RESOURCE_TYPES)[number];
